export const DOCUMENT_LIFECYCLE_ACTIONS = [
  "submit",
  "approve",
  "reject",
  "cancel",
  "return",
  "reopen",
  "reverse",
  "amend",
] as const;

export type DocumentLifecycleAction = (typeof DOCUMENT_LIFECYCLE_ACTIONS)[number];

export const DOCUMENT_LIFECYCLE_REVERSE_POLICIES = [
  "none",
  "status_only",
  "reverse_gl",
  "reverse_stock",
  "credit_or_debit_note",
  "amendment",
  "custom",
] as const;

export type DocumentLifecycleReversalPolicy =
  (typeof DOCUMENT_LIFECYCLE_REVERSE_POLICIES)[number];

export interface DocumentLifecycleActionContract {
  label: string;
  from: readonly string[];
  to: string;
  docStatus?: 0 | 1 | 2 | null;
  reversalPolicy?: DocumentLifecycleReversalPolicy;
  requiresReason?: boolean;
  activityAction?: string;
}

export interface DocumentLifecycleContract {
  statusField: string;
  docStatusField?: string | null;
  draftStates: readonly string[];
  submittedStates: readonly string[];
  cancelledStates: readonly string[];
  returnedStates?: readonly string[];
  immutableStates: readonly string[];
  editAllowedStates: readonly string[];
  actions: Partial<Record<DocumentLifecycleAction, DocumentLifecycleActionContract>>;
}

export interface CreateSubmitCancelLifecycleOptions {
  statusField?: string;
  docStatusField?: string | null;
  draftState?: string;
  submittedStates?: readonly string[];
  cancelledState?: string;
  submitLabel?: string;
  cancelLabel?: string;
  reversalPolicy?: DocumentLifecycleReversalPolicy;
  cancelFromStates?: readonly string[];
  immutableStates?: readonly string[];
  editAllowedStates?: readonly string[];
  submitDocStatus?: 0 | 1 | 2 | null;
  cancelDocStatus?: 0 | 1 | 2 | null;
}

export interface LifecycleActionCheckParams {
  lifecycle: DocumentLifecycleContract;
  action: DocumentLifecycleAction;
  currentStatus?: string | null;
  currentDocStatus?: number | null;
  doctypeLabel?: string;
}

const FIELD_NAME_RE = /^[a-z][a-z0-9_]*$/;
const DOC_STATUS_TO_STATE: Record<number, string> = {
  0: "draft",
  1: "submitted",
  2: "cancelled",
};

function cloneStringArray(values: readonly string[]): string[] {
  return [...values];
}

function isLifecycleAction(value: string): value is DocumentLifecycleAction {
  return (DOCUMENT_LIFECYCLE_ACTIONS as readonly string[]).includes(value);
}

function isReversalPolicy(value: unknown): value is DocumentLifecycleReversalPolicy {
  return (
    typeof value === "string" &&
    (DOCUMENT_LIFECYCLE_REVERSE_POLICIES as readonly string[]).includes(value)
  );
}

function normalizeActionContract(
  action: DocumentLifecycleAction,
  value: DocumentLifecycleActionContract,
): DocumentLifecycleActionContract {
  return Object.freeze({
    label: value.label,
    from: Object.freeze(cloneStringArray(value.from)),
    to: value.to,
    docStatus: value.docStatus ?? null,
    reversalPolicy: value.reversalPolicy ?? "none",
    requiresReason: value.requiresReason ?? false,
    activityAction: value.activityAction ?? action,
  });
}

export function createSubmitCancelLifecycleContract(
  options: CreateSubmitCancelLifecycleOptions = {},
): DocumentLifecycleContract {
  const draftState = options.draftState ?? "draft";
  const submittedStates = options.submittedStates ?? ["submitted"];
  const cancelledState = options.cancelledState ?? "cancelled";
  const cancelFromStates =
    options.cancelFromStates ?? [draftState, ...submittedStates];
  const immutableStates =
    options.immutableStates ?? [...submittedStates, cancelledState];
  const editAllowedStates = options.editAllowedStates ?? [draftState];
  const reversalPolicy = options.reversalPolicy ?? "status_only";
  const submitDocStatus =
    options.submitDocStatus === undefined ? 1 : options.submitDocStatus;
  const cancelDocStatus =
    options.cancelDocStatus === undefined ? 2 : options.cancelDocStatus;

  return normalizeLifecycleContract({
    statusField: options.statusField ?? "status",
    docStatusField:
      options.docStatusField === undefined ? "doc_status" : options.docStatusField,
    draftStates: [draftState],
    submittedStates,
    cancelledStates: [cancelledState],
    immutableStates,
    editAllowedStates,
    actions: {
      submit: {
        label: options.submitLabel ?? "ยืนยัน",
        from: [draftState],
        to: submittedStates[0] ?? "submitted",
        docStatus: submitDocStatus,
        reversalPolicy: "none",
        activityAction: "submitted",
      },
      cancel: {
        label: options.cancelLabel ?? "ยกเลิก",
        from: cancelFromStates,
        to: cancelledState,
        docStatus: cancelDocStatus,
        reversalPolicy,
        requiresReason: reversalPolicy !== "status_only",
        activityAction: "cancelled",
      },
    },
  });
}

export function normalizeLifecycleContract(
  value: DocumentLifecycleContract,
): DocumentLifecycleContract {
  const actions: Partial<Record<DocumentLifecycleAction, DocumentLifecycleActionContract>> = {};
  for (const [key, action] of Object.entries(value.actions)) {
    if (!isLifecycleAction(key)) continue;
    if (!action) continue;
    actions[key] = normalizeActionContract(key, action);
  }

  return Object.freeze({
    statusField: value.statusField,
    docStatusField: value.docStatusField ?? null,
    draftStates: Object.freeze(cloneStringArray(value.draftStates)),
    submittedStates: Object.freeze(cloneStringArray(value.submittedStates)),
    cancelledStates: Object.freeze(cloneStringArray(value.cancelledStates)),
    returnedStates: value.returnedStates
      ? Object.freeze(cloneStringArray(value.returnedStates))
      : undefined,
    immutableStates: Object.freeze(cloneStringArray(value.immutableStates)),
    editAllowedStates: Object.freeze(cloneStringArray(value.editAllowedStates)),
    actions: Object.freeze(actions),
  });
}

export function parseLifecycleContract(
  value: unknown,
): DocumentLifecycleContract | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const actionsCandidate = candidate.actions;
  if (
    typeof candidate.statusField !== "string" ||
    !Array.isArray(candidate.draftStates) ||
    !Array.isArray(candidate.submittedStates) ||
    !Array.isArray(candidate.cancelledStates) ||
    !Array.isArray(candidate.immutableStates) ||
    !Array.isArray(candidate.editAllowedStates) ||
    !actionsCandidate ||
    typeof actionsCandidate !== "object" ||
    Array.isArray(actionsCandidate)
  ) {
    return null;
  }

  const actions: Partial<Record<DocumentLifecycleAction, DocumentLifecycleActionContract>> = {};
  for (const [key, raw] of Object.entries(actionsCandidate)) {
    if (!isLifecycleAction(key)) continue;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const action = raw as Record<string, unknown>;
    if (
      typeof action.label !== "string" ||
      !Array.isArray(action.from) ||
      typeof action.to !== "string"
    ) {
      continue;
    }
    const from = action.from.filter((v): v is string => typeof v === "string");
    if (from.length === 0) continue;
    const docStatus =
      action.docStatus === 0 || action.docStatus === 1 || action.docStatus === 2
        ? action.docStatus
        : null;
    const reversalPolicy = isReversalPolicy(action.reversalPolicy)
      ? action.reversalPolicy
      : "none";
    actions[key] = {
      label: action.label,
      from,
      to: action.to,
      docStatus,
      reversalPolicy,
      requiresReason: action.requiresReason === true,
      activityAction:
        typeof action.activityAction === "string"
          ? action.activityAction
          : key,
    };
  }

  return normalizeLifecycleContract({
    statusField: candidate.statusField,
    docStatusField:
      typeof candidate.docStatusField === "string"
        ? candidate.docStatusField
        : null,
    draftStates: candidate.draftStates.filter((v): v is string => typeof v === "string"),
    submittedStates: candidate.submittedStates.filter((v): v is string => typeof v === "string"),
    cancelledStates: candidate.cancelledStates.filter((v): v is string => typeof v === "string"),
    returnedStates: Array.isArray(candidate.returnedStates)
      ? candidate.returnedStates.filter((v): v is string => typeof v === "string")
      : undefined,
    immutableStates: candidate.immutableStates.filter((v): v is string => typeof v === "string"),
    editAllowedStates: candidate.editAllowedStates.filter((v): v is string => typeof v === "string"),
    actions,
  });
}

export function validateLifecycleContract(
  value: DocumentLifecycleContract | null | undefined,
): string[] {
  if (!value) return [];
  const errors: string[] = [];
  const checkField = (field: string | null | undefined, label: string) => {
    if (field === null || field === undefined) return;
    if (!FIELD_NAME_RE.test(field)) {
      errors.push(`${label} "${field}" must be snake_case`);
    }
  };
  const checkStates = (states: readonly string[], label: string) => {
    if (!Array.isArray(states) || states.length === 0) {
      errors.push(`${label} must contain at least one state`);
      return;
    }
    for (const state of states) {
      if (typeof state !== "string" || state.length === 0) {
        errors.push(`${label} contains an invalid state`);
      }
    }
  };

  checkField(value.statusField, "lifecycle.statusField");
  checkField(value.docStatusField, "lifecycle.docStatusField");
  checkStates(value.draftStates, "lifecycle.draftStates");
  checkStates(value.submittedStates, "lifecycle.submittedStates");
  checkStates(value.cancelledStates, "lifecycle.cancelledStates");
  checkStates(value.immutableStates, "lifecycle.immutableStates");
  checkStates(value.editAllowedStates, "lifecycle.editAllowedStates");

  if (!value.actions.submit) {
    errors.push("lifecycle.actions.submit is required for a lifecycle contract");
  }
  for (const [key, action] of Object.entries(value.actions)) {
    if (!isLifecycleAction(key)) {
      errors.push(`lifecycle.actions contains unknown action "${key}"`);
      continue;
    }
    if (!action) continue;
    if (!action.label || typeof action.label !== "string") {
      errors.push(`lifecycle.actions.${key}.label is required`);
    }
    if (!Array.isArray(action.from) || action.from.length === 0) {
      errors.push(`lifecycle.actions.${key}.from must contain at least one state`);
    }
    if (!action.to || typeof action.to !== "string") {
      errors.push(`lifecycle.actions.${key}.to is required`);
    }
    if (
      action.docStatus !== undefined &&
      action.docStatus !== null &&
      action.docStatus !== 0 &&
      action.docStatus !== 1 &&
      action.docStatus !== 2
    ) {
      errors.push(`lifecycle.actions.${key}.docStatus must be 0, 1, or 2`);
    }
    if (
      action.reversalPolicy !== undefined &&
      !isReversalPolicy(action.reversalPolicy)
    ) {
      errors.push(`lifecycle.actions.${key}.reversalPolicy is invalid`);
    }
  }

  return errors;
}

export function getLifecycleState(
  lifecycle: DocumentLifecycleContract,
  currentStatus?: string | null,
  currentDocStatus?: number | null,
): string | null {
  if (currentStatus) return currentStatus;
  if (currentDocStatus === null || currentDocStatus === undefined) return null;
  return DOC_STATUS_TO_STATE[currentDocStatus] ?? null;
}

export function assertLifecycleActionAllowed(
  params: LifecycleActionCheckParams,
): DocumentLifecycleActionContract {
  const action = params.lifecycle.actions[params.action];
  if (!action) {
    throw new Error(
      `VALIDATION_ERROR: ${params.doctypeLabel ?? "Document"} does not support ${params.action}`,
    );
  }
  const state = getLifecycleState(
    params.lifecycle,
    params.currentStatus,
    params.currentDocStatus,
  );
  if (!state || !action.from.includes(state)) {
    throw new Error(
      `VALIDATION_ERROR: ${params.doctypeLabel ?? "Document"} cannot ${params.action} from state ${state ?? "unknown"}`,
    );
  }
  return action;
}

export function isLifecycleStateImmutable(
  lifecycle: DocumentLifecycleContract,
  currentStatus?: string | null,
  currentDocStatus?: number | null,
): boolean {
  const state = getLifecycleState(lifecycle, currentStatus, currentDocStatus);
  return !!state && lifecycle.immutableStates.includes(state);
}
